import { Fragment, ReactNode, useId, useRef } from 'react';
import { Box, paletteColors } from '@evaneos/b2b-ui';

import { EditableTrigger } from './EditableTrigger';
import type { EditableTriggerProps } from './EditableTrigger';

type EditableItem = {
    id?: string;
    trigger: ReactNode;
} & Pick<
    EditableTriggerProps,
    | 'editor'
    | 'popoverWidth'
    | 'placement'
    | 'onBeforeClose'
    | 'disablePopoverMinHeight'
    | 'hoverHint'
    | 'disabled'
>;

type EditableGroupProps = {
    labelId: string;
    items: EditableItem[];
    separator?: ReactNode;
    disabled?: boolean;
};

/**
 * Editable group component for use in a DefinitionList.
 * Wraps multiple editable triggers in a single <dd> tag with a shared hover effect.
 * Use this when you have multiple related values that can be edited independently.
 * Popovers anchor to the group container.
 */
export function EditableGroup({
    labelId,
    items,
    separator,
    disabled,
}: Readonly<EditableGroupProps>) {
    const containerRef = useRef<HTMLElement>(null);
    const generatedId = useId();

    return (
        <Box
            ref={containerRef}
            component="dd"
            sx={{
                flex: 1,
                m: 0,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                padding: 0.5,
                minHeight: '3.2rem',
                borderRadius: 1,
                '&:hover': { backgroundColor: disabled ? undefined : paletteColors.neutral[100] },
            }}
        >
            {items.map((item, index) => {
                const itemId = item.id ?? `${generatedId}-${index}`;
                return (
                    <Fragment key={itemId}>
                        <EditableTrigger
                            labelId={labelId}
                            baseId={itemId}
                            editor={item.editor}
                            placement={item.placement}
                            popoverWidth={item.popoverWidth ?? 'trigger'}
                            disablePopoverMinHeight={item.disablePopoverMinHeight}
                            onBeforeClose={item.onBeforeClose}
                            anchorEl={() => containerRef.current}
                            disabled={disabled || item.disabled}
                        >
                            {item.trigger}
                        </EditableTrigger>
                        {separator && index < items.length - 1 && separator}
                    </Fragment>
                );
            })}
        </Box>
    );
}
